import { SnackbarApiErrorMessage } from '@ui-framework/index';
import { CUTOFF_KEY, WEEKDAY_KEYS } from './constants';
import { getDefaultCutoffForWeekday } from './lib';

const DefaultSchedule = {
  /* Handling sending the response differently, we get 404 when there is no Current Schedule */
  async fetch({ params, defaultScheduleId = null, locationId = null }) {
    const locationParam = locationId ? `/location/${locationId}` : '';
    const url = `${params.getApiUrl()}/sxcl/v1/availability/service-providers/${params.pageId}${locationParam}/default-schedules${defaultScheduleId ? `/${defaultScheduleId}` : ''}`;
    const response = await params.get(url);
    if (defaultScheduleId) {
      return response;
    }
    if (response?.ok) {
      return response.data;
    }
    params.snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  },
  async upsert({ params, data, defaultScheduleId, locationId = null }) {
    const locationParam = locationId ? `/location/${locationId}` : '';
    const url = `${params.getApiUrl()}/sxcl/v1/availability/service-providers/${params.pageId}${locationParam}/default-schedules${defaultScheduleId ? `/${defaultScheduleId}` : ''}`;
    const method = defaultScheduleId ? params.put : params.post;
    const response = await method(url, data);
    return response;
  },
  async delete({ params, defaultScheduleId, locationId = null }) {
    const locationParam = locationId ? `/location/${locationId}` : '';
    const url = `${params.getApiUrl()}/sxcl/v1/availability/service-providers/${params.pageId}${locationParam}/default-schedules/${defaultScheduleId}`;
    const response = await params?.remove(url);
    if (response?.ok) {
      return true;
    }
    params?.snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  },
  getDefaultValues(slots) {
    const initialValue = {};
    WEEKDAY_KEYS.forEach(wk => {
      initialValue[wk] = {
        [CUTOFF_KEY]: { ...getDefaultCutoffForWeekday(wk) },
      };
      slots.forEach(sk => {
        initialValue[wk][sk?.code] = true;
      });
    });
    return initialValue;
  },
  getUpsertObject(data, slotTypes) {
    const schedules = Object.entries(data.schedule).map(([key, schedule]) => {
      // Find only slots that are true
      const slots = Object.entries(schedule).reduce(
        (acc, [slotKey, slotValue]) => {
          if (slotKey !== 'cutoff' && slotValue === true) {
            return [...acc, slotKey];
          }
          return acc;
        },
        [],
      );

      // If slots are not there then dont send slots and cutoff data
      const slotsFound = slots.map(s => {
        return {
          slotTypeCode: slotTypes.find(st => st.code === s)?.code,
        };
      });

      return {
        dayOfWeek: key,
        ...(slotsFound.length > 0 && {
          cutoff: {
            cutoffDayOffset: schedule?.cutoff?.daysBefore,
            cutoffTime: schedule?.cutoff?.time,
          },
        }),
        ...(slotsFound.length === 0 && { isClosed: true }),
        ...(slotsFound.length > 0 && { slots: slotsFound }),
      };
    });

    return {
      effectiveStartDate: data?.effectiveStartDate,
      effectiveEndDate: data?.effectiveEndDate || null,
      schedules,
      ...(data?.recordVersion && { recordVersion: data?.recordVersion }),
    };
  },
  parseSuccessResponseForNextValue({ schedule, slotTypes, serviceProviderId }) {
    const { schedules = [], ...otherProps } = schedule;
    const getElmentByDayOfWeek = wk => schedules.find(s => s.dayOfWeek === wk);
    const getSlotElmentByCodeAndWeek = (code, slotData) =>
      slotData.find(s => s.slotTypeCode === code);

    const schedulesFound = WEEKDAY_KEYS.reduce((acc, wk) => {
      const weekdaySchedule = getElmentByDayOfWeek(wk);
      const slotsInfo = slotTypes.reduce((slotAcc, item) => {
        const slotFound = getSlotElmentByCodeAndWeek(
          item?.code,
          weekdaySchedule?.slots || [],
        );
        return {
          ...slotAcc,
          [item.code]: !!slotFound,
        };
      }, {});

      const result = {
        ...acc,
        [wk]: {
          cutoff: {
            daysBefore: weekdaySchedule?.cutoff?.cutoffDayOffset,
            time: weekdaySchedule?.cutoff?.cutoffTime,
          },
          ...slotsInfo,
        },
      };
      return result;
    }, {});

    return {
      schedule: schedulesFound,
      ...otherProps,
      serviceProviderId,
    };
  },
};

export default DefaultSchedule;
